// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGaugeValueDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGaugeValueDetails model
 */
!(function() {
  var m = require('mobx');
  const { find, isNil, get, nth, values, has, reduce } = require('lodash/fp');

  var setValue = require('share/utils/Izenda.setValue');
  var trackable = require('share/tracking/Izenda.Trackable');
  var CONSTANTS = require('logic/data/Izenda.ReportPartGaugeConstants');
  var THRESHOLD_SETTINGS = CONSTANTS.THRESHOLD_SETTINGS;
  var THRESHOLD_VALUES = CONSTANTS.THRESHOLD_VALUES;
  var ReportPartElement = require('logic/models/Izenda.ReportPartElement');
  function ReportPartGaugeValueDetails(initData) {
    var props = {
      scale: {
        from: null,
        to: null
      },
      unitLabel: '',
      thresholds: {
        setting: '',
        levels: []
      },
      supplementaryKPI: {
        valueType: '',
        unitLabel: '',
        value: null,
        element: null
      },

      isStaticSupKPI: function() {
        return this.supplementaryKPI.valueType === THRESHOLD_SETTINGS.STATIC.value;
      },

      isStaticThreshold: function() {
        return this.thresholds.setting === THRESHOLD_SETTINGS.STATIC.value;
      }
    };

    m.extendObservable(trackable(this), props);
    initData && this.updateData(initData);

    this.cleanDirty();
  }

  /**
   * Update value of data formating
   * @param  {object} newVal
   * @return {null}
   */
  ReportPartGaugeValueDetails.prototype.updateData = m.action(function(newVal) {
    // scale
    setValue(this.scale, 'from', get('scale.from', newVal));
    setValue(this.scale, 'to', get('scale.to', newVal));

    // unit label
    setValue(this, 'unitLabel', get('unitLabel', newVal));

    // thresholds
    setValue(this.thresholds, 'setting', get('thresholds.setting', newVal));
    this.setThresholdSettingLevels(get('thresholds.levels', newVal));

    // supplementaryKPI
    setValue(this.supplementaryKPI, 'valueType', get('supplementaryKPI.valueType', newVal));
    setValue(this.supplementaryKPI, 'unitLabel', get('supplementaryKPI.unitLabel', newVal));
    setValue(this.supplementaryKPI, 'value', get('supplementaryKPI.value', newVal));

    // convert supplementaryKPI element JSON to Report Part Element
    var supplementaryKPIElement = this.supplementaryKPI.element;
    if (supplementaryKPIElement) {
      supplementaryKPIElement.readData(get('supplementaryKPI.element', newVal));
    } else {
      var newElement = new ReportPartElement();
      var supplementaryKPIData = get('supplementaryKPI.element', newVal);

      supplementaryKPIData &&
        newElement.readData(supplementaryKPIData) &&
        setValue(this.supplementaryKPI, 'element', newElement);
    }

    this.markDirty();
  });

  /**
   * Get dirty status.
   */
  ReportPartGaugeValueDetails.prototype.checkDirty = function() {
    return this.isDirty;
  };

  /**
   * Clean dirty status.
   */
  ReportPartGaugeValueDetails.prototype.cleanDirty = function() {
    this.resetDirty();
  };

  /**
   * Set supplementary KPI,
   * Sup-KPI = static > value is number
   * Sup-KPI = dynamic > value is field
   * @param  {any} newValue
   * @return {undefined}
   */
  ReportPartGaugeValueDetails.prototype.setSupKpiValue = function(newValue) {
    var supplementaryKPI = this.supplementaryKPI;
    var isStatic = this.isStaticSupKPI;

    supplementaryKPI[isStatic ? 'value' : 'element'] = newValue && newValue.value ? newValue.value : newValue;
  };

  /**
   * Set threshold setting levels
   * @param {object} newVal
   */
  ReportPartGaugeValueDetails.prototype.setThresholdSettingLevels = m.action(function(levels) {
    var createReportPartElement = function(data) {
      var element = new ReportPartElement();
      element.readData(data);
      return element;
    };
    var thresholdLevels = !isNil(levels)
      ? levels.map(function(level) {
          level.elementUpper && (level.elementUpper = createReportPartElement(level.elementUpper));
          level.element && (level.element = createReportPartElement(level.element));
          return level;
        })
      : [];

    setValue(this.thresholds, 'levels', m.observable(thresholdLevels));
  });

  /**
   * set threshold value
   * @param {Boolean} isThresholdValue
   * @param {object}  thresholdData
   * @param {string}  dataKey
   * @param {any}  dataVal
   * @param {Boolean} isUpperBound
   * @param {object}  settingInfo
   */
  ReportPartGaugeValueDetails.prototype.setThresholdValue = function(
    isThresholdValue,
    thresholdData,
    dataKey,
    dataVal,
    isUpperBound,
    settingInfo
  ) {
    var thresholds = this.thresholds;
    var thresholdName = thresholdData.name;
    var isStatic = this.isStaticThreshold;

    dataKey = dataKey || getValueKey(isStatic, isUpperBound);

    m.transaction(
      function() {
        // TODO apply logic to each change of threshold
        thresholdData[dataKey] = dataVal;

        if (!isThresholdValue) {
          return;
        }

        switch (thresholdName) {
          case THRESHOLD_VALUES.LOW.value:
            setThresholdVal('TARGET', dataVal);
            break;
          case THRESHOLD_VALUES.TARGET.value:
            setThresholdVal(isUpperBound ? 'HIGH' : 'LOW', dataVal, !isUpperBound);
            break;
          case THRESHOLD_VALUES.HIGH.value:
            setThresholdVal('TARGET', dataVal, true);
            break;
          default:
          // do nothing
        }

        this.markDirty();
      }.bind(this)
    );

    function setThresholdVal(thresholdKey, value, isUpperBound) {
      thresholdKey = THRESHOLD_VALUES[thresholdKey].value;

      var threshold =
        find(
          {
            name: thresholdKey
          },
          thresholds.levels
        ) || {};
      var fieldKey = getValueKey(isStatic, isUpperBound);

      threshold[fieldKey] = value;
    }

    function getValueKey(isStatic, isUpperBound) {
      return (isStatic ? 'value' : 'element') + (isUpperBound ? 'Upper' : '');
    }
  };

  /**
   * set threshold setting
   * @param {number|object} newVal
   */
  ReportPartGaugeValueDetails.prototype.setThresholdSetting = function(newVal) {
    var thresholds = this.thresholds;

    m.transaction(
      function() {
        switch (newVal) {
          case THRESHOLD_SETTINGS.STATIC.value:
          case THRESHOLD_SETTINGS.DYNAMIC.value:
            thresholds.levels = initThresholdValues.call(this);
            break;
          default:
            thresholds.levels = [];
        }

        thresholds.setting = newVal;

        this.markDirty();
      }.bind(this)
    );

    /**
     * init data for threshold item
     * @return {[type]} [description]
     */
    function initThresholdValues() {
      return values(CONSTANTS.THRESHOLD_VALUES).map(function(thresholdInfo) {
        return {
          name: thresholdInfo.value,
          color: thresholdInfo.defautColor,
          operator: thresholdInfo.defautOperator,
          value: null,
          valueUpper: null,
          element: null,
          elementUpper: null
        };
      });
    }
  };

  /**
   * Reset scale data: scale from & scale to
   */
  ReportPartGaugeValueDetails.prototype.resetScaleData = function() {
    m.transaction(
      function() {
        this.scale.from = this.scale.to = null;

        this.markDirty();
      }.bind(this)
    );
  };

  /**
   * Reset supplementary KPI setting
   */
  ReportPartGaugeValueDetails.prototype.resetSupplementaryKPISetting = function() {
    this.setSupplementaryKPISetting();
  };

  /**
   * set supplementary KPI setting
   * @param {objct} newVal
   */
  ReportPartGaugeValueDetails.prototype.setSupplementaryKPISetting = function(newVal) {
    var supplementaryKPI = this.supplementaryKPI;
    var SuppSettings = CONSTANTS.SUPP_SETTINGS;

    newVal = newVal || '';

    m.transaction(
      function() {
        switch (newVal) {
          case SuppSettings.STATIC:
            supplementaryKPI.element = {};
            break;

          case SuppSettings.DYNAMIC:
            supplementaryKPI.value = null;
            break;

          default:
            supplementaryKPI.unitLabel = '';
            supplementaryKPI.value = null;
            supplementaryKPI.element = null;
        }

        supplementaryKPI.valueType = newVal;

        this.markDirty();
      }.bind(this)
    );
  };

  /**
   * set value based on key
   * @param  {string} keyPath
   * @param  {any} newVal
   * @return {undefined}
   */
  ReportPartGaugeValueDetails.prototype.setDataByKey = function(keyPath, newVal) {
    if (!has(keyPath)) {
      return;
    }

    m.transaction(
      function() {
        var paths = keyPath.split('.');
        var changedKey = paths.pop();
        var des = this;

        des = reduce(
          function(des, path) {
            des = des[path];
            return des;
          },
          des,
          paths
        );

        des[changedKey] = newVal;

        this.markDirty();
      }.bind(this)
    );
  };

  /**
   * return threshold by range
   * @return {object}
   */
  ReportPartGaugeValueDetails.prototype.getThresholdByRange = function() {
    var from, to;

    var thresholdsByRange = [];

    this.thresholds.levels.forEach(function(thresold) {
      from = thresold.value || thresold.element;
      to = thresold.valueUpper || thresold.elementUpper;

      if (from || to) {
        thresholdsByRange.push({
          from: from,
          to: to,
          color: thresold.color
        });
      }
    });
    return thresholdsByRange;
  };

  /**
   * Validation threshold value
   * @param  {string} fieldValidation
   * @return {object}
   */
  ReportPartGaugeValueDetails.prototype.validateThreshold = function(fieldValidation) {
    var thresholds = this.thresholds;
    var levels = thresholds.levels;

    var low = get('valueUpper', nth(0, levels));
    var high = get('value', nth(2, levels));
    var scaleFrom = get('scale.from', this);
    var scaleTo = get('scale.to', this);

    var messageLow = [];
    var messageHigh = [];
    var messageFrom = [];
    var messageTo = [];

    // Validate value of low threshold with high threshold
    if (!isNil(low) && !isNil(high) && low >= high) {
      messageLow.push(CONSTANTS.Message.M01);
      messageHigh.push(CONSTANTS.Message.M01);
    }
    // Validate value of low threshold with scale to
    if (!isNil(low) && !isNil(scaleTo) && low > scaleTo) {
      messageLow.push(CONSTANTS.Message.M04);
      messageTo.push(CONSTANTS.Message.M04);
    }
    // Validate value of high threshold with scale from
    if (!isNil(high) && !isNil(scaleFrom) && scaleFrom > high) {
      messageHigh.push(CONSTANTS.Message.M04);
      messageFrom.push(CONSTANTS.Message.M04);
    }
    // Validate value of low threshold with scale from
    if (!isNil(low) && !isNil(scaleFrom) && scaleFrom > low) {
      messageLow.push(CONSTANTS.Message.M04);
      messageFrom.push(CONSTANTS.Message.M04);
    }
    // Validate value of high threshold with scale to
    if (!isNil(high) && !isNil(scaleTo) && high > scaleTo) {
      messageTo.push(CONSTANTS.Message.M04);
      messageHigh.push(CONSTANTS.Message.M04);
    }

    switch (fieldValidation) {
      case 'low':
        if (messageLow.length > 0) return { key: '', messages: messageLow };
        break;
      case 'high':
        if (messageHigh.length > 0) return { key: '', messages: messageHigh };
        break;
      case 'from':
        if (messageFrom.length > 0) return { key: fieldValidation, messages: messageFrom };
        break;
      case 'to':
        if (messageTo.length > 0) return { key: fieldValidation, messages: messageTo };
        break;
    }
  };

  module.exports = ReportPartGaugeValueDetails;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartGaugeValueDetails.js